// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
// Define a type for anything that can be deeply searched.
// This allows for objects with string keys and any value type.
// Using 'unknown' ensures type safety when handling potentially unknown structures.
// type DeepSearchable =
//   | string
//   | number
//   | boolean
//   | null
//   | undefined
//   | DeepSearchable[]
//   | { [key: string]: DeepSearchable }; // This is the crucial index signature

// The function now accepts 'unknown' because it can truly be anything.
// We then use type guards to narrow down the type.
export const deepExactSearch = (obj: unknown, searchTerm: string): boolean => {
  const lowerCaseSearchTerm = searchTerm.toLowerCase();

  if (searchTerm === null || searchTerm.length === 0) {
    return true; // Empty search term matches everything
  }

  // Base case 1: If the current 'obj' is a primitive value (string, number, boolean)
  if (typeof obj === 'string' || typeof obj === 'number' || typeof obj === 'boolean') {
    const stringifiedValue = String(obj);
    return stringifiedValue.toLowerCase().includes(lowerCaseSearchTerm);
  }

  // Base case 2: If 'obj' is null or undefined, we can't search inside it
  if (obj === null || obj === undefined) {
    return false;
  }

  // Handle Arrays
  // TypeScript knows 'obj' is not null/undefined here.
  if (Array.isArray(obj)) {
    // We now know 'obj' is an array. TypeScript infers `obj is any[]`.
    // We can then safely iterate and recursively call.
    // eslint-disable-next-line no-restricted-syntax
    for (const item of obj) {
      // The recursive call deepExactSearch(item, searchTerm) is safe because 'item'
      // will again be checked against all the 'DeepSearchable' types.
      if (deepExactSearch(item, searchTerm)) {
        return true; // Found in an array element
      }
    }
    return false;
  }

  // Handle Objects
  // At this point, `obj` is `object` and is not `null` and not an `Array`.
  // We need to ensure it's a plain object that can be iterated.
  if (typeof obj === 'object') {
    // This is where you might still get issues if your 'DeadTask' (or other specific types)
    // doesn't have an index signature `[key: string]: any`.
    // The safest way is to cast it to `DeepSearchable` or `Record<string, unknown>` for iteration.
    const iterableObj = obj as { [key: string]: unknown }; // Cast to a type with an index signature

    // eslint-disable-next-line no-restricted-syntax
    for (const key in iterableObj) {
      if (Object.prototype.hasOwnProperty.call(iterableObj, key)) {
        // Now 'iterableObj[key]' is safely accessible and its type is 'unknown'.
        const value = iterableObj[key];
        // Recursive call is safe.
        if (deepExactSearch(value, searchTerm)) {
          return true; // Found in an object property
        }
      }
    }
    return false;
  }

  // If obj is a function, symbol, bigint, or any other unhandled type, return false.
  return false;
};
